// src/vdr/sterling.ts
import * as XLSX from "xlsx";
import JSZip from "jszip";
import type { IndexRow } from "./providers";

// Accepts codes like "1", "1.1", "1.1.2" (pure digits + dots)
const CODE_RE = /^\d+(?:\.\d+)*$/;

// Newer Sterling exports prefix every code with the root's number and a
// space (e.g. "1 1.2.3"). Drop that prefix so we get "1.2.3".
function normalizeCode(raw: string): string {
  return raw.trim().replace(/^\d+\s+(?=\d)/, "");
}

// Anything we can't read stops the import, so we never build a partial tree.
const CONTACT_ADMIN = "Please contact your File Pigeon admin.";

function unrecognizedCodeError(code: string, name: string): Error {
  return new Error(
    `This Sterling index has an index number File Pigeon doesn't recognize ("${code || "(blank)"}" for "${name}"). ${CONTACT_ADMIN}`
  );
}

/**
 * Sterling VDR Index Structure:
 * - Column B (index 1): Type ("Folder" or "Document")
 * - Column C (index 2): Code (e.g., "1", "1.1", "1.2.3")
 * - Column D (index 3): Name
 */

// --- CSV parsing ---
function parseCsv(text: string): IndexRow[] {
  const lines = text.split(/\r?\n/).filter(l => l.trim().length);
  if (!lines.length) return [];

  const rows: IndexRow[] = [];

  // Skip first line (header)
  for (let i = 1; i < lines.length; i++) {
    const cols = safeSplit(lines[i]).map(c => c.trim());

    const typeCell = (cols[1] ?? "").toString().trim().toLowerCase(); // Column B
    const code = normalizeCode((cols[2] ?? "").toString());           // Column C
    const name = (cols[3] ?? "").toString().trim();                   // Column D

    if (typeCell !== "folder" && typeCell !== "document") continue;
    if (!CODE_RE.test(code)) throw unrecognizedCodeError(code, name);

    const isDocument = typeCell === "document";
    rows.push({ code, title: name || code, meta: { isDocument } });
  }

  return rows;
}

// --- XLS/XLSX parsing ---
async function parseXlsx(file: File): Promise<IndexRow[]> {
  const buf = await file.arrayBuffer();

  // Some Sterling exports contain malformed zip64 size headers on tiny
  // internal entries (e.g. _rels/.rels), which crashes XLSX.read with an
  // out-of-memory error. Re-packing through JSZip normalizes the archive.
  const zip = await JSZip.loadAsync(buf);
  const cleanBuf = await zip.generateAsync({ type: "arraybuffer" });

  const wb = XLSX.read(cleanBuf, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];

  // Some exports also report a stale/incorrect sheet dimension, which
  // truncates sheet_to_json's output. Recompute the real range from the
  // actual populated cells instead of trusting ws["!ref"].
  const cellAddrs = Object.keys(ws).filter(k => k[0] !== "!");
  const range = cellAddrs.reduce(
    (r, addr) => {
      const c = XLSX.utils.decode_cell(addr);
      r.s.r = Math.min(r.s.r, c.r);
      r.s.c = Math.min(r.s.c, c.c);
      r.e.r = Math.max(r.e.r, c.r);
      r.e.c = Math.max(r.e.c, c.c);
      return r;
    },
    { s: { r: Infinity, c: Infinity }, e: { r: -Infinity, c: -Infinity } }
  );

  const table: any[][] = XLSX.utils.sheet_to_json(ws, {
    header: 1,
    defval: "",
    range: XLSX.utils.encode_range(range),
  });

  const rows: IndexRow[] = [];

  // Skip first row (header)
  for (let i = 1; i < table.length; i++) {
    const row = table[i] ?? [];
    const typeCell = String(row[1] ?? "").trim().toLowerCase(); // Column B
    const code = normalizeCode(String(row[2] ?? ""));            // Column C
    const name = String(row[3] ?? "").trim();                    // Column D

    if (typeCell !== "folder" && typeCell !== "document") continue;
    if (!CODE_RE.test(code)) throw unrecognizedCodeError(code, name);

    const isDocument = typeCell === "document";
    rows.push({ code, title: name || code, meta: { isDocument } });
  }

  return rows;
}

/**
 * Parse a Sterling index file (CSV/XLS/XLSX) into normalized rows.
 * - Column B = Type ("Folder" or "Document")
 * - Column C = Code (hierarchical like "1", "1.1", "1.2.3")
 * - Column D = Name
 * - Keeps both folders and documents, marking documents with meta.isDocument
 */
export async function parseIndexFile(file: File): Promise<IndexRow[]> {
  const ext = file.name.toLowerCase().split(".").pop();
  console.log(`🧾 [sterling] parseIndexFile: name="${file.name}" ext="${ext}" size=${(file as any)?.size ?? "?"}`);

  let rows: IndexRow[] = [];
  if (ext === "csv") {
    const text = await file.text();
    rows = parseCsv(text);
  } else if (ext === "xlsx" || ext === "xls") {
    rows = await parseXlsx(file);
  } else {
    const text = await file.text();
    rows = parseCsv(text);
  }

  console.log(`   ↳ parsed rows: ${rows.length}`);
  if (rows.length > 0) {
    const sample = rows.slice(0, Math.min(3, rows.length));
    console.log("   ↳ sample:", sample);
  } else {
    console.warn("   ⚠️ No valid Sterling rows parsed. Expected columns: B=Type, C=Code, D=Name");
    throw new Error(`No folders or documents found in this Sterling index. ${CONTACT_ADMIN}`);
  }
  return rows;
}

// Simple CSV splitter (handles quoted fields)
function safeSplit(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];

    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}
